import { Box } from '@mui/material';
import { MotionConfig } from 'motion/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AboutCard, BelongsCard, LogTimeCard, PeopleCard, WhenCard } from './cards';
import type { CardEditProps } from './cards';
import { UnsavedChangesDialog, useCardEditor } from './editing';
import type { CardId } from './editing';
import { AboutForm, BelongsForm, LogTimeForm, PeopleForm, WhenForm } from './forms';
import { DetailsPanel, DetailsRail } from './panel';
import { PeopleModal } from './people';
import { compareReach, peopleWhoCanLogTime, reachPeople } from './sampleData';
import {
  appendChange,
  defaultDetailsValues,
  detailsSample,
  diffChanges,
  MISSING_CARD,
  missingItems,
  useChangeLog,
  useDetailsValues,
} from './state';
import type { DetailsValues, MissingItem } from './state';

import { useProject } from '@/api';
import { Panels } from '@/components/layouts';
import { ProjectHeaderBar } from '@/components/project';
import { todayIso } from '@/lib/format';

/**
 * Project details (docs/briefs/project-details-v2/brief.md): a one-page
 * description of the project — who it's for, who runs it, when, what it
 * covers, who can log time — read like a one-pager.
 *
 * Five cards in two columns, about 3:2: Who's involved · Where it belongs,
 * then When · What it's about, then Who can log time at full width (Mette,
 * 3 Nov — decision 18; the grid is unchanged). Each card edits in a modal it
 * grows into (decision 16), one at a time.
 *
 * The grid fills the page's width at every size, like the header above it —
 * no max width (Mette, 30 Oct: the reference's 1280px cap left the cards
 * standing still on a wide window).
 */
/**
 * Two columns once the grid itself is wide enough, whatever the window: the
 * right panel takes a third of the page when open, so a window breakpoint
 * would squeeze two columns into it. A container query asks the grid's own
 * width instead.
 */
const TWO_COLUMNS_MIN_PX = 880;

const GRID_WRAPPER_SX = { containerType: 'inline-size' } as const;

const GRID_SX = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: 'minmax(0, 1fr)',
  [`@container (min-width: ${String(TWO_COLUMNS_MIN_PX)}px)`]: {
    gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
  },
} as const;

/** Each card's element id — where the panel's links scroll to. */
const CARD_ELEMENT_ID = {
  about: 'details-card-about',
  people: 'details-card-people',
  logTime: 'details-card-log-time',
  when: 'details-card-when',
  belongs: 'details-card-belongs',
} as const satisfies Record<CardId, string>;

/** Each card's title key — for the unsaved-changes dialog's question. */
const TITLE_KEY = {
  about: 'about.Title',
  people: 'people.Title',
  logTime: 'logTime.Title',
  when: 'when.Title',
  belongs: 'belongs.Title',
} as const satisfies Record<CardId, string>;

export const ProjectDetailsPage = () => {
  const { t } = useTranslation('pmDetails');
  const { projectId = '' } = useParams<{ projectId?: string }>();
  const { data } = useProject(projectId);
  const document = data?.document;
  const sample = detailsSample();
  const values = useDetailsValues(projectId, document);
  const defaults = defaultDetailsValues(sample, document);
  const customerId = document?.customer?.tlpId;
  // What's changed (slice 6): each save that changed something is logged,
  // and the rail's dot says so until the panel is next open.
  const [unseenChanges, setUnseenChanges] = useState(false);
  const logSave = useCallback(
    (card: CardId, before: DetailsValues, after: DetailsValues) => {
      const count = (from: DetailsValues) =>
        peopleWhoCanLogTime(sample, {
          scope: from.logTimeScope,
          legalEntityId: from.legalEntityId,
          departmentId: from.departmentId,
        }).size;
      const items = diffChanges(card, before, after, {
        before: count(before),
        after: count(after),
      });
      if (items.length === 0) return;
      appendChange(projectId, {
        id: `c${String(Date.now())}`,
        card,
        items,
        at: new Date().toISOString(),
      });
      setUnseenChanges(true);
    },
    [projectId, sample],
  );
  const markSeen = useCallback(() => setUnseenChanges(false), []);
  const changes = useChangeLog(projectId);
  const missing = missingItems(values);

  const editor = useCardEditor(projectId, values, logSave);
  const { state } = editor;
  const draft = { ...values, ...state.draft };
  const editFor = (
    card: CardId,
    form: CardEditProps['form'],
    saveLabel?: string,
  ): CardEditProps => ({
    form,
    editing: state.editing === card,
    saved: state.saved === card,
    onEdit: () => editor.request(card),
    onCancel: editor.cancel,
    onRequestClose: editor.requestClose,
    onSave: editor.save,
    saveLabel,
  });

  /**
   * The panel's links: bring the card into view and open it — through the
   * same request as ✎. A missing
   * contact also puts the cursor in the contact picker.
   */
  const goToCard = (card: CardId, focusId?: string) => {
    window.document.getElementById(CARD_ELEMENT_ID[card])?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    editor.request(card);
    if (focusId) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => window.document.getElementById(focusId)?.focus()),
      );
    }
  };
  const goToMissing = (item: MissingItem) =>
    goToCard(MISSING_CARD[item], item === 'contact' ? 'details-contact' : undefined);

  // What it's about: the empty state's Attach file opens the form on the file
  // picker. Cleared as soon as any other card is opened.
  const [browseOnOpen, setBrowseOnOpen] = useState(false);
  const openAbout = (browse: boolean) => {
    setBrowseOnOpen(browse);
    editor.request('about');
  };

  // Who can log time: what the draft level would change, against what is
  // saved (slice 4). 200 people — cheap enough to derive on every render.
  const [seeWhoOpen, setSeeWhoOpen] = useState(false);
  const savedSetting = {
    scope: values.logTimeScope,
    legalEntityId: values.legalEntityId,
    departmentId: values.departmentId,
  };
  const draftSetting = { ...savedSetting, scope: draft.logTimeScope };
  const logTimeChange =
    state.editing === 'logTime' && draft.logTimeScope !== values.logTimeScope
      ? compareReach(
          peopleWhoCanLogTime(sample, savedSetting),
          peopleWhoCanLogTime(sample, draftSetting),
        )
      : null;
  const contacts =
    (customerId === undefined ? undefined : sample.contacts[customerId]) ??
    sample.contacts.default ??
    [];

  return (
    // The card-to-modal morph honours the OS's reduced-motion setting, as Web's does.
    <MotionConfig reducedMotion="user">
      <Box sx={{ height: '100%', minHeight: 0 }} data-automation-id="project-details-page">
        {/*
        The right panel is signals only and starts collapsed (§3); collapsed,
        its rail still shows what is missing and whether anything changed.
      */}
        <Panels autoSaveId="pm-project-details-panels" defaultCollapsed collapseDrawerOnEscape>
          <Panels.Main>
            <ProjectHeaderBar projectName={document?.name ?? '—'} projectCode={document?.code} />
            {document ? (
              <Box sx={GRID_WRAPPER_SX}>
                <Box sx={GRID_SX} data-automation-id="project-details-cards">
                  <PeopleCard
                    values={values}
                    sample={sample}
                    contacts={contacts}
                    customerName={document.customer?.snapshot.name}
                    edit={editFor(
                      'people',
                      <PeopleForm
                        draft={draft}
                        sample={sample}
                        contacts={contacts}
                        customerName={document.customer?.snapshot.name}
                        onChange={editor.change}
                      />,
                    )}
                  />
                  <BelongsCard
                    values={values}
                    sample={sample}
                    edit={editFor(
                      'belongs',
                      <BelongsForm draft={draft} sample={sample} onChange={editor.change} />,
                    )}
                  />
                  <WhenCard
                    values={values}
                    defaults={defaults}
                    today={todayIso()}
                    edit={editFor('when', <WhenForm draft={draft} onChange={editor.change} />)}
                  />
                  <AboutCard
                    values={values}
                    edit={{
                      ...editFor(
                        'about',
                        <AboutForm
                          draft={draft}
                          onChange={editor.change}
                          browseOnOpen={browseOnOpen}
                        />,
                      ),
                      onEdit: () => openAbout(false),
                    }}
                    onAttach={() => openAbout(true)}
                  />
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <LogTimeCard
                      values={values}
                      defaults={defaults}
                      sample={sample}
                      edit={editFor(
                        'logTime',
                        <LogTimeForm
                          draft={draft}
                          sample={sample}
                          change={logTimeChange}
                          onChange={editor.change}
                          onSeeWho={() => setSeeWhoOpen(true)}
                        />,
                        logTimeChange
                          ? t('logTime.SaveCount', { count: logTimeChange.after })
                          : undefined,
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Panels.Main>
          <Panels.Drawer
            label={t('panel.DrawerLabel')}
            collapsedContent={
              <DetailsRail missingCount={missing.length} unseenChanges={unseenChanges} />
            }
          >
            <DetailsPanel
              missing={missing}
              changes={changes}
              onGoMissing={goToMissing}
              onSeen={markSeen}
              sample={sample}
              contacts={contacts}
              cardTitle={(card) => t(TITLE_KEY[card])}
              onGo={(card) => goToCard(card)}
            />
          </Panels.Drawer>
        </Panels>
        {logTimeChange ? (
          <PeopleModal
            open={seeWhoOpen}
            change={logTimeChange}
            people={
              seeWhoOpen ? reachPeople(sample, savedSetting, draftSetting, logTimeChange) : []
            }
            showChange
            title={t('logTime.SeeWhoTitle')}
            onClose={() => setSeeWhoOpen(false)}
          />
        ) : null}
        <UnsavedChangesDialog
          open={state.confirmClose}
          cardTitle={state.editing === null ? '' : t(TITLE_KEY[state.editing])}
          onKeepEditing={editor.keepEditing}
          onDiscard={editor.cancel}
          onSave={editor.save}
        />
      </Box>
    </MotionConfig>
  );
};
